import { useCallback, useEffect, useRef, useState } from 'react';

export type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'stopped' | 'error';

export interface RecorderError {
  message: string;
  kind: 'permission' | 'unsupported' | 'unknown';
}

export interface UseRecorderResult {
  status: RecorderStatus;
  elapsedMs: number;
  error: RecorderError | null;
  /** Live while `status === 'recording'`, for a level meter. Null otherwise. */
  analyser: AnalyserNode | null;
  /** Set once, when recording stops. Cleared by `discard`. */
  file: File | null;
  start: () => Promise<void>;
  stop: () => void;
  discard: () => void;
}

/**
 * Records from the microphone, as an alternative to choosing a file that
 * already exists.
 *
 * The result is a plain `File`, so it drops straight into the same
 * `prepareAudio` / `useUpload` pipeline a chosen file goes through — there is
 * no second upload path to keep in sync with the first, and no server change
 * needed. `ring_buffer_prefix_ms` stays 0 for a recording made this way, same
 * as for an uploaded file: capture begins when the button is pressed, and the
 * moments before that are genuinely not in the file. Only a true device ring
 * buffer (the mobile client's job) can claim otherwise.
 */

/** Guard against a forgotten tab recording for an hour. Not a product limit. */
const MAX_DURATION_MS = 10 * 60 * 1000;

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}

export function useRecorder(): UseRecorderResult {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<RecorderError | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const startedAtRef = useRef(0);
  const rafRef = useRef(0);

  const teardownMedia = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (audioCtxRef.current) {
      void audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    recorderRef.current = null;
    setAnalyser(null);
  }, []);

  // Release the microphone if the component unmounts mid-recording — a tab
  // change must not leave the mic light on.
  useEffect(() => teardownMedia, [teardownMedia]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  const tick = useCallback(() => {
    const elapsed = performance.now() - startedAtRef.current;
    setElapsedMs(elapsed);
    if (elapsed >= MAX_DURATION_MS) {
      stop();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [stop]);

  const start = useCallback(async () => {
    setError(null);
    setFile(null);
    setElapsedMs(0);
    setStatus('requesting');

    // Mirrors the guard in prepare.ts for SubtleCrypto: on an insecure origin
    // (anything but https:// or localhost) mediaDevices is simply absent, and
    // the failure otherwise reads as a confusing TypeError.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus('error');
      setError({
        kind: 'unsupported',
        message: 'This browser cannot record audio here. Choose a file instead.',
      });
      return;
    }

    const mimeType = pickMimeType();
    if (!mimeType) {
      setStatus('error');
      setError({
        kind: 'unsupported',
        message: 'This browser cannot encode a recording. Choose a file instead.',
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const AudioCtx =
        window.AudioContext ??
        (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const node = audioCtx.createAnalyser();
      node.fftSize = 256;
      node.smoothingTimeConstant = 0.75;
      source.connect(node);
      setAnalyser(node);

      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        // Strip codec parameters for the stored File — `audio/webm` travels
        // better than `audio/webm;codecs=opus` as a Content-Type on the
        // presigned upload, and prepareAudio decodes either way.
        const baseType = mimeType.split(';')[0]!;
        const blob = new Blob(chunksRef.current, { type: baseType });
        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        setFile(
          new File([blob], `recording-${stamp}.${extensionFor(mimeType)}`, { type: baseType }),
        );
        setStatus('stopped');
        teardownMedia();
      };

      recorder.start();
      startedAtRef.current = performance.now();
      setStatus('recording');
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      teardownMedia();
      setStatus('error');
      const name = e instanceof Error ? e.name : '';
      setError({
        kind: name === 'NotAllowedError' || name === 'SecurityError' ? 'permission' : 'unknown',
        message:
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'Microphone access was not allowed. Enable it for this site, or choose a file instead.'
            : name === 'NotFoundError'
              ? 'No microphone was found. Choose a file instead.'
              : 'Could not start recording. Choose a file instead.',
      });
    }
  }, [teardownMedia, tick]);

  const discard = useCallback(() => {
    teardownMedia();
    setStatus('idle');
    setElapsedMs(0);
    setFile(null);
    setError(null);
  }, [teardownMedia]);

  return { status, elapsedMs, error, analyser, file, start, stop, discard };
}
